import { useMutation } from '@tanstack/react-query';
import { fetchAddressResolution, retryAddressLookup } from '@services/addressLookupService';
import type { ResolvedAddress } from '@t/address.types';

type Args = {
  sessionToken: string;
  onResolved: (address: ResolvedAddress) => void;
  onEpisodeEnded: () => void;
};

type UseResolutionMutationResult = {
  mutate: (predictionId: string) => void;
  isPending: boolean;
  isError: boolean;
  error: Error | null;
};

/**
 * A mutation rather than a query, for two reasons: resolving is the billed call
 * and is triggered by one deliberate click, and the vendor's licence terms on
 * storing resolved address data have not been read — so nothing keyed and
 * reusable is kept.
 */
export const useResolutionMutation = ({
  sessionToken,
  onResolved,
  onEpisodeEnded,
}: Args): UseResolutionMutationResult => {
  const mutation = useMutation<ResolvedAddress, Error, string>({
    mutationFn: (predictionId) => fetchAddressResolution({ id: predictionId, sessionToken }),
    retry: retryAddressLookup,
    // Narrowed to one argument on purpose: the library also passes the request
    // variables and its own context, and neither belongs on a seam typed to
    // receive a provider-neutral address.
    onSuccess: (address) => onResolved(address),
    // On settled rather than on success, because a failed attempt has already
    // reached the provider under this episode's token — carrying it on would
    // hand the next episode a token the provider has already closed.
    onSettled: () => onEpisodeEnded(),
  });

  return {
    mutate: (predictionId) => mutation.mutate(predictionId),
    isPending: mutation.isPending,
    isError: mutation.isError,
    error: mutation.error,
  };
};
